import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  LinearProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Sms as SmsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Autorenew as AutorenewIcon,
} from "@mui/icons-material";
import { useDataProvider, useNotify } from "react-admin";

interface NotificationStat {
  status: string;
  type: string;
  channel: string;
  count: string;
}

export const NotificationStats = () => {
  const [stats, setStats] = useState<NotificationStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const statsData = await (dataProvider as any).getNotificationStats();
      setStats(statsData);
    } catch (err) {
      setError("Ошибка при загрузке статистики уведомлений");
      notify("Ошибка при загрузке статистики уведомлений", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "retrying":
        return "warning";
      case "pending":
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <CheckCircleIcon />;
      case "failed":
        return <ErrorIcon />;
      case "retrying":
        return <AutorenewIcon />;
      case "pending":
      default:
        return <ScheduleIcon />;
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "email":
        return <EmailIcon />;
      case "push":
        return <NotificationsIcon />;
      case "sms":
        return <SmsIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "welcome":
        return "Добро пожаловать";
      case "password_change":
        return "Смена пароля";
      case "password_reset":
        return "Сброс пароля";
      case "follow":
        return "Подписка";
      case "invite":
        return "Приглашение";
      case "checkin_reminder":
        return "Напоминание о чекине";
      case "challenge_start":
        return "Начало челленджа";
      case "challenge_complete":
        return "Завершение челленджа";
      case "supplement_order_confirmed":
        return "Подтверждение заказа";
      case "payment_success":
        return "Успешная оплата";
      case "payment_failed":
        return "Ошибка оплаты";
      default:
        return type;
    }
  };

  // Группируем статистику по статусам
  const statsByStatus = stats.reduce(
    (acc, stat) => {
      if (!acc[stat.status]) {
        acc[stat.status] = 0;
      }
      acc[stat.status] += parseInt(stat.count);
      return acc;
    },
    {} as Record<string, number>,
  );

  // Группируем статистику по каналам
  const statsByChannel = stats.reduce(
    (acc, stat) => {
      if (!acc[stat.channel]) {
        acc[stat.channel] = 0;
      }
      acc[stat.channel] += parseInt(stat.count);
      return acc;
    },
    {} as Record<string, number>,
  );

  // Группируем статистику по типам
  const statsByType = stats.reduce(
    (acc, stat) => {
      if (!acc[stat.type]) {
        acc[stat.type] = 0;
      }
      acc[stat.type] += parseInt(stat.count);
      return acc;
    },
    {} as Record<string, number>,
  );

  const totalNotifications = stats.reduce(
    (sum, stat) => sum + parseInt(stat.count),
    0,
  );

  if (loading && stats.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadStats}>
            Повторить
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: 2 }}>
      {/* Заголовок */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4" component="h1" color="primary">
            Статистика уведомлений
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadStats}
            disabled={loading}
            size="large"
          >
            Обновить
          </Button>
        </Box>
      </Paper>

      {/* Общая статистика */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4 }}>
        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                  }}
                >
                  <NotificationsIcon />
                </Box>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Всего уведомлений
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totalNotifications}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{ height: 4, borderRadius: 2 }}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "success.main",
                    color: "white",
                  }}
                >
                  <CheckCircleIcon />
                </Box>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Отправлено
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {statsByStatus.sent || 0}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={totalNotifications > 0 ? ((statsByStatus.sent || 0) / totalNotifications) * 100 : 0}
                color="success"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "error.main",
                    color: "white",
                  }}
                >
                  <ErrorIcon />
                </Box>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Ошибки
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {statsByStatus.failed || 0}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={totalNotifications > 0 ? ((statsByStatus.failed || 0) / totalNotifications) * 100 : 0}
                color="error"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "warning.main",
                    color: "white",
                  }}
                >
                  <ScheduleIcon />
                </Box>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Ожидают
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {statsByStatus.pending || 0}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={totalNotifications > 0 ? ((statsByStatus.pending || 0) / totalNotifications) * 100 : 0}
                color="warning"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {/* Статистика по каналам */}
        <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                По каналам
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {Object.entries(statsByChannel).map(([channel, count]) => {
                  const percentage = totalNotifications > 0 ? (count / totalNotifications) * 100 : 0;
                  return (
                    <Box key={channel}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Box display="flex" alignItems="center">
                          {getChannelIcon(channel)}
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: "medium" }}>
                            {channel.toUpperCase()}
                          </Typography>
                        </Box>
                        <Chip 
                          label={count} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Статистика по типам */}
        <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                По типам
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {Object.entries(statsByType)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 8)
                  .map(([type, count]) => {
                    const percentage = totalNotifications > 0 ? (count / totalNotifications) * 100 : 0;
                    return (
                      <Box key={type}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                            {getTypeLabel(type)}
                          </Typography>
                          <Chip 
                            label={count} 
                            size="small" 
                            color="secondary"
                            variant="outlined"
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          color="secondary"
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    );
                  })}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Детальная статистика */}
        <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Детальная статистика
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Статус</TableCell>
                      <TableCell>Канал</TableCell>
                      <TableCell>Тип</TableCell>
                      <TableCell align="right">Кол-во</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.slice(0, 15).map((stat, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(stat.status)}
                            label={stat.status}
                            size="small"
                            color={getStatusColor(stat.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getChannelIcon(stat.channel)}
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {stat.channel}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {getTypeLabel(stat.type)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={stat.count} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
